/**
 * User Activity Tracker (client beacon)
 *
 * Presence is recorded server-side now (see packages/bff): the BFF writes the
 * login row at the OAuth callback and the logout row at /auth/logout, each with
 * the session's authoritative identity. The only thing the browser knows that
 * the server doesn't is which SPA route the user navigated to, so the client's
 * sole remaining job is to beacon area changes to `POST /api/activity`, which
 * writes the Dataverse row and stamps the user's last-seen.
 *
 * Why this moved off the client: the previous browser-direct-to-Dataverse path
 * lost events to a client-init race and swallowed write failures, so the User
 * Activity admin tile undercounted users who plainly had live sessions. The
 * beacon needs only the session cookie (already present) + CSRF token (added by
 * bffFetch), so there's no init race to lose the event to.
 *
 * Retained coarse-mode rule: at most one area_visit per area per 60s per tab.
 *
 * Tab clicks: the browser also knows which tab someone picks inside a page,
 * which page visits cannot show because tab state never reaches the URL. One
 * document-level listener records those as tab_view, so no page has to opt in
 * and a tab added later is counted the day it ships. Same coarse rule: at most
 * one tab_view per page and tab per 60s.
 */

import { bffFetch } from '@shared/api';
import { appIdForPath } from '../model/apps';
import { tabLabelFromText, tabViewArea } from '../model/tabLabel';

const SESSION_ID_KEY = 'vera-activity-session-id';
const AREA_THROTTLE_MS = 60_000;
const TAB_THROTTLE_MS = 60_000;

/**
 * Fluent v9 tabs, scoped by Fluent's static class so any other role="tab"
 * widget (a grid's column menu) is not counted as a page tab.
 */
const TAB_SELECTOR = '.fui-Tab[role="tab"]';

/**
 * Fluent renders a tab's content twice, the second copy hidden to reserve the
 * width of the bold selected state, so the tab's own textContent reads
 * 'SummarySummary'. This is the visible copy.
 */
const TAB_CONTENT_SELECTOR = '.fui-Tab__content:not(.fui-Tab__content--reserved-space)';

type UserIdentity = {
  oid: string;
  name: string;
  email: string;
};

let currentIdentity: UserIdentity | null = null;
let lastAreaEmit: { area: string; at: number } | null = null;
/** The page area of the latest navigation, unthrottled: tab rows are named under it. */
let currentArea = '';
const lastTabEmit = new Map<string, number>();
let tabListenerInstalled = false;

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

function currentPath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

/**
 * Derive the app id from the current URL, since the consolidated shell mounts
 * every module under one SPA. This used to hard-code /sales, /ops and /catalog
 * with a fallback to the shell, which meant the ten modules added since all
 * recorded as `vera-shell` and no app filter could find them (ADO #1558). It
 * now reads the module catalog, so a new module is attributed the day it is
 * mounted, with no second list to remember.
 */
function effectiveApp(): string {
  return appIdForPath(currentPath());
}

async function beacon(input: {
  app: string;
  area: string;
  path: string;
  sessionId: string;
  eventType?: 'area_visit' | 'tab_view';
  tab?: string;
}): Promise<void> {
  try {
    await bffFetch('/api/activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch (err) {
    // Telemetry failures are non-fatal, but unlike the old client-direct
    // path, the server also logs the write outcome, so failures aren't silent.
    if (typeof console !== 'undefined') console.warn('[user-activity] beacon failed', err);
  }
}

function tabLabelOf(tab: Element): string {
  return (
    tabLabelFromText(tab.querySelector(TAB_CONTENT_SELECTOR)?.textContent) ||
    tabLabelFromText(tab.getAttribute('aria-label'))
  );
}

function onDocumentClick(event: MouseEvent) {
  if (!currentIdentity || !(event.target instanceof Element)) return;
  const tab = event.target.closest(TAB_SELECTOR);
  // Capture phase runs before React re-renders, so aria-selected still
  // describes the tab strip before this click: re-clicking the open tab is not
  // a choice worth a row.
  if (!tab || tab.getAttribute('aria-selected') === 'true') return;
  const label = tabLabelOf(tab);
  if (!label) return;
  const pathAtClick = currentPath();
  // Some tab strips navigate. Let this click's render settle first: if the
  // route changed, the new page's own area_visit already records the choice.
  setTimeout(() => {
    if (currentPath() === pathAtClick) userActivityTracker.trackTabView(label);
  }, 0);
}

export const userActivityTracker = {
  /**
   * Called by AuthProvider once the user is known. The login row itself is
   * written server-side at the OAuth callback; here we just record that the
   * user is present so area beacons can start firing.
   */
  setIdentity(identity: UserIdentity | null) {
    currentIdentity = identity;
    if (identity && !tabListenerInstalled && typeof document !== 'undefined') {
      document.addEventListener('click', onDocumentClick, true);
      tabListenerInstalled = true;
    }
  },

  /** Called on area/nav changes; throttles per area, then beacons the BFF. */
  trackAreaVisit(area: string) {
    if (!currentIdentity || !area) return;
    currentArea = area;
    const app = effectiveApp();

    const now = Date.now();
    if (
      lastAreaEmit &&
      lastAreaEmit.area === area &&
      now - lastAreaEmit.at < AREA_THROTTLE_MS
    ) {
      return;
    }
    lastAreaEmit = { area, at: now };

    void beacon({ app, area, path: currentPath(), sessionId: getSessionId() });
  },

  /**
   * A tab picked inside the current page. Called by the document listener; a
   * page with a non-Fluent tab strip can call it directly.
   */
  trackTabView(tab: string) {
    if (!currentIdentity || !currentArea || !tab) return;
    const key = `${currentArea}|${tab}`;
    const now = Date.now();
    const last = lastTabEmit.get(key);
    if (last !== undefined && now - last < TAB_THROTTLE_MS) return;
    lastTabEmit.set(key, now);

    void beacon({
      app: effectiveApp(),
      area: tabViewArea(currentArea, tab),
      path: currentPath(),
      sessionId: getSessionId(),
      eventType: 'tab_view',
      tab,
    });
  },

  /**
   * Called by AuthProvider on sign-out. The logout row is written server-side
   * by POST /auth/logout; here we only clear local markers so the next sign-in
   * starts a fresh session id.
   */
  async trackLogout() {
    currentIdentity = null;
    lastAreaEmit = null;
    currentArea = '';
    lastTabEmit.clear();
    try {
      sessionStorage.removeItem(SESSION_ID_KEY);
    } catch {
      /* sessionStorage unavailable: nothing to clear */
    }
  },
};
